<template>
    <div class="fia-page-wrapper">
        <app-new-breadcrumb :id="this.id"></app-new-breadcrumb>

        <div class="fia-details-page" v-if="menu">
            <div class="fia-details-inner">
                <!-- Page header -->
                <div class="page-header">
                    <div class="page-header-inner">
                        <h1 class="page-title">{{ menu.title }}</h1>
                        <p class="page-subtitle" v-if="htmlContent">{{ htmlContent }}</p>
                        <p class="page-subtitle" v-else-if="menu.subtitle">{{ menu.subtitle }}</p>
                    </div>
                </div>
    
                <!-- Children grid (excludes banner children) -->
                <div class="page-body" v-if="regularChildren && regularChildren.length">
                    <div class="children-grid">
                        <app-fia-intelligence-card
                            v-for="(child, index) in regularChildren"
                            :key="child.id"
                            :item="child"
                            :accent-index="index"
                        />
                    </div>
                </div>

                <!-- CTA Banner: driven by children with banner tag -->
                <div
                    v-for="bannerChild in bannerChildren"
                    :key="'banner-' + bannerChild.id"
                    class="cta-banner"
                >
                    <div class="cta-banner-left">
                        <div class="cta-icon"><i class="fa fa-book"></i></div>
                        <div class="cta-text">
                            <h3 class="cta-title">{{ bannerChild.title }}</h3>
                            <p class="cta-desc" v-if="bannerChild.subtitle">{{ bannerChild.subtitle }}</p>
                        </div>
                    </div>
                    <a
                        v-if="bannerChild.url && bannerChild.url.startsWith('http')"
                        :href="bannerChild.url"
                        target="_blank"
                        class="cta-button"
                    >{{ $t('preview') }} →</a>
                    <router-link
                        v-else-if="bannerChild.url"
                        :to="{ name: bannerChild.url }"
                        class="cta-button"
                    >{{ $t('preview') }} →</router-link>
                    <router-link
                        v-else
                        :to="{ name: 'fiaIntelligenceDetails', params: { id: bannerChild.id, slug: bannerChild.slug } }"
                        class="cta-button"
                    >{{ $t('preview') }} →</router-link>
                </div>

                <!-- Operative Services: fallback when no banner children -->
                <section v-if="!bannerChildren.length" class="operative-services-section">
                    <h2 class="os-title">{{ $t('operativeServices') }}</h2>
                    <div class="os-grid">
                        <app-services-card
                            v-for="item in highlightedServicesItems"
                            :key="item.id"
                            :item="item"
                        />
                    </div>
                </section>
            </div>

        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Breadcrumb from '../../common/Breadcrumb.vue'
import FiaIntelligenceCard from './FiaIntelligenceCard.vue'
import HighlightedServicesCard from '../../content/HighlightedServicesCard.vue'

export default {
    name: 'FiaIntelligenceDetails',

    components: {
        appNewBreadcrumb: Breadcrumb,
        appFiaIntelligenceCard: FiaIntelligenceCard,
        appServicesCard: HighlightedServicesCard,
    },

    props: ['id'],

    data() {
        return {
            menu: null,
            children: [],
            htmlContent: ''
        }
    },

    computed: {
        ...mapGetters(['getContentById', 'getChildrenByParentId', 'getSelectedLanguage', 'highlightedServicesItems']),

        isExternalLink() {
            return this.menu && this.menu.url && this.menu.url.startsWith('http')
        },

        bannerChildren() {
            const bannerTagId = this.$store.state.tagIds.banner
            return this.children.filter(child => {
                if (!child.tags) return false
                try {
                    return JSON.parse(child.tags).some(t => t.tagId === bannerTagId)
                } catch { return false }
            })
        },

        regularChildren() {
            const bannerTagId = this.$store.state.tagIds.banner
            return this.children.filter(child => {
                if (!child.tags) return true
                try {
                    return !JSON.parse(child.tags).some(t => t.tagId === bannerTagId)
                } catch { return true }
            })
        }
    },

    methods: {
        ...mapActions(['fetchContentById']),

        loadData() {
            if (!this.id) return
            console.log(this.id)

            this.menu = this.getContentById(this.id)
            this.children = this.getChildrenByParentId(this.id)

            this.fetchContentById(this.id).then((response) => {
                if (response) {
                    if (this.menu) {
                        this.menu.title = response.title
                        this.menu.subtitle = response.subtitle
                        this.menu.url = response.url
                        this.menu.slug = response.slug
                    }
                    const plain = (response.content || '').replace(/<[^>]*>/g, '').trim()
                    this.htmlContent = plain
                }
            })
        }
    },

    created() {
        this.loadData()
    },

    watch: {
        id() {
            this.loadData()
        },
        getSelectedLanguage() {
            this.loadData()
        }
    }
}
</script>

<style scoped>
.fia-page-wrapper {
    width: 100%;
}

.fia-details-page {
    width: 100%;
    box-sizing: border-box;
    padding: 0 7% 60px;
}

.fia-details-inner {
    max-width: 1440px;
    margin: 0 auto;
}
/* .fia-details-page {
    width: 100%;
    box-sizing: border-box;
    padding: 0 0 60px;
}

@media (min-width: 1440px) {
    .fia-details-page {
        max-width: 1440px;
        margin: 0 auto;
        padding: 0 7% 60px;
    }
} */

/* ── Page header ── */
.page-header {
    padding: 36px 0 28px;
    border-bottom: 1px solid #e8eef6;
    margin-bottom: 32px;
}

.page-title {
    font-size: 32px;
    font-weight: 700;
    color: var(--main-color);
    margin: 0 0 8px;
    line-height: 1.2;
}

.page-subtitle {
    font-size: 15px;
    color: #64748b;
    margin: 0;
    max-width: 680px;
    line-height: 1.6;
}

/* ── Children grid ── */
.page-body {
    margin-bottom: 40px;
}

.children-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

@media (max-width: 1200px) {
    .children-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 900px) {
    .children-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 560px) {
    .children-grid {
        grid-template-columns: 1fr;
    }
}

/* ── Operative Services ── */
.operative-services-section {
    margin-top: 8px;
}

.os-title {
    font-size: 20px;
    font-weight: 600;
    color: var(--main-color);
    margin: 0 0 16px;
}

.os-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
}

@media (max-width: 900px) {
    .os-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 560px) {
    .os-grid {
        grid-template-columns: 1fr;
    }
}

/* ── CTA Banner ── */
.cta-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    background: #f0f4fb;
    border: 1px solid #dbe7ff;
    border-radius: 14px;
    padding: 24px 28px;
    margin-top: 8px;
}

.cta-banner-left {
    display: flex;
    align-items: center;
    gap: 20px;
    flex: 1;
}

.cta-icon {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #dbeafe;
    color: var(--main-color);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    flex-shrink: 0;
}

.cta-title {
    font-size: 17px;
    font-weight: 700;
    color: var(--main-color);
    margin: 0 0 4px;
}

.cta-desc {
    font-size: 14px;
    color: #64748b;
    margin: 0;
    line-height: 1.5;
}

.cta-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 12px 24px;
    background: var(--main-color);
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.cta-button:hover {
    opacity: 0.88;
    transform: translateX(2px);
    text-decoration: none;
    color: #fff;
}

.cta-button--placeholder {
    background: var(--main-color);
}

@media (max-width: 700px) {
    .cta-banner {
        flex-direction: column;
        align-items: flex-start;
    }

    .cta-button {
        width: 100%;
        justify-content: center;
    }
}
</style>